// SCR-STAFF-510 — خروجی پرونده (US-CLN-101؛ AC-CLN-101.1؛ C-AGENT-1478): انتخاب بخش‌ها (سوابق، مواجهات، تشخیص‌ها، جلسات)، بازه تاریخ و هدف؛ PDF با
// سربرگ شعبه و امضای تولیدکننده در پرونده؛ یادداشت HighlySensitive فقط با تأیید یادداشت و درخواست صریح (BR-CLN-101)؛ تحویل دانلود یا پرتال با تاریخچه.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnRecordExportPurpose, type ClnRecordExportSection, type ClnRecordExportView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export const SCR_STAFF_510 = 'SCR-STAFF-510';

const SECTIONS: readonly NonNullable<ClnRecordExportSection>[] = ['History', 'Encounters', 'Diagnoses', 'Sessions'];
const PURPOSES: readonly NonNullable<ClnRecordExportPurpose>[] = ['PatientRequest', 'Referral', 'Legal', 'Other'];

export function RecordExportPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const [sections, setSections] = useState<NonNullable<ClnRecordExportSection>[]>(['History', 'Encounters', 'Diagnoses']);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [purpose, setPurpose] = useState<NonNullable<ClnRecordExportPurpose>>('PatientRequest');
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const listKey = ['clinical', tenantId, 'record-exports', patientId] as const;
  const list = useQuery({ queryKey: listKey, queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/record-exports', { params: { path: { patientId } } })) });
  const generate = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/record-exports', {
          params: { path: { patientId } },
          body: { sections, from: from || null, to: to || null, planId: null, imageIds: null, includeSensitive, purpose },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (created) => {
      setNotice(t('cln:record_export.generated', { number: created.number }));
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const share = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/clinical/record-exports/{id}:share', { params: { path: { id } }, body: { channel: 'Portal' } })),
    onSuccess: () => {
      setNotice(t('cln:record_export.shared'));
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const download = async (row: ClnRecordExportView) => {
    const response = await tenantApi.GET('/api/v1/clinical/record-exports/{id}/content', { params: { path: { id: row.id } }, parseAs: 'blob' });
    if (response.response.ok && response.data && typeof URL.createObjectURL === 'function') {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data as unknown as Blob);
      link.download = `${row.number}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    }
    void queryClient.invalidateQueries({ queryKey: listKey });
  };
  const toggle = (value: NonNullable<ClnRecordExportSection>) => setSections((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  const columns: DataTableColumn<ClnRecordExportView>[] = [
    { id: 'number', headerKey: 'cln:record_export.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'sections', headerKey: 'cln:record_export.sections', cell: (row) => row.sections.map((item) => t(`cln:record_export.section.${item}`)).join(t('cln:common.list_separator')) },
    { id: 'period', headerKey: 'cln:record_export.period', cell: (row) => (row.from || row.to ? `${row.from ? toJalali(row.from) : '…'} – ${row.to ? toJalali(row.to) : '…'}` : t('cln:record_export.all_time')) },
    { id: 'generated', headerKey: 'cln:record_export.generated_at', cell: (row) => `${toJalaliDateTime(row.generatedAt, { timeZone })} · ${row.generatedByName ?? ''}` },
    {
      id: 'deliveries',
      headerKey: 'cln:record_export.deliveries',
      cell: (row) => (row.deliveries.length ? row.deliveries.map((item) => t(`cln:record_export.channel.${item.channel}`)).join(t('cln:common.list_separator')) : '—'),
    },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => void download(row)} data-testid="cln-record-export-download">
            {t('cln:record_export.download')}
          </Button>
          {full && access.can('doc.document.share') && (
            <Button size="small" onClick={() => share.mutate(row.id)} data-testid="cln-record-export-share">
              {t('cln:record_export.share_portal')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  return (
    <Stack spacing={1.5} data-screen-id={SCR_STAFF_510} data-testid="cln-record-export">
      {full && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <FormLabel component="legend">{t('cln:record_export.choose_sections')}</FormLabel>
            <FormGroup row>
              {SECTIONS.map((value) => (
                <FormControlLabel key={value} control={<Checkbox checked={sections.includes(value)} onChange={() => toggle(value)} />} label={t(`cln:record_export.section.${value}`)} data-testid={`cln-record-export-section-${value}`} />
              ))}
            </FormGroup>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField type="date" label={t('cln:record_export.from')} value={from} onChange={(event) => setFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'cln-record-export-from' } }} />
              <TextField type="date" label={t('cln:record_export.to')} value={to} onChange={(event) => setTo(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'cln-record-export-to' } }} />
              <TextField select label={t('cln:record_export.purpose')} value={purpose} onChange={(event) => setPurpose(event.target.value as NonNullable<ClnRecordExportPurpose>)} sx={{ minWidth: 200 }}>
                {PURPOSES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`cln:record_export.purposes.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <FormControlLabel control={<Checkbox checked={includeSensitive} onChange={(event) => setIncludeSensitive(event.target.checked)} />} label={t('cln:record_export.include_sensitive')} />
            {includeSensitive && <Typography variant="caption" color="text.secondary">{t('cln:record_export.sensitive_hint')}</Typography>}
            {generate.error ? <ErrorState error={generate.error} compact /> : null}
            <Button variant="contained" sx={{ alignSelf: 'flex-start' }} disabled={sections.length === 0 || generate.isPending} onClick={() => generate.mutate()} data-testid="cln-record-export-generate">
              {t('cln:record_export.generate')}
            </Button>
          </Stack>
        </Paper>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="cln-record-export-notice">
          {notice}
        </Alert>
      )}
      {share.error ? <ErrorState error={share.error} compact /> : null}
      <DataTable captionKey="cln:record_export.history" columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="cln:record_export.empty" />
    </Stack>
  );
}
